import { createContext, useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@resource-kit/ui/react';
import { http } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { useBetterAuth } from '@hooks/useAuth';
import { setMerchantTz } from '@/lib/merchant-tz';
import { AuthLoadingFallback } from '@components/auth/AuthLoadingFallback';

interface TimezoneSnapshot {
  timezone: string;
  offset: string;
  abbreviation: string;
  now: string;
}

interface MerchantTimezoneContextValue {
  timezone: string;
  abbreviation: string;
  offset: string;
}

/** The session tenant's zone; served after sign-in. */
const SESSION_TIMEZONE_PATH = '/api/system/timezone';

const GATE_STATUS = {
  loading: 'loading',
  ready: 'ready',
  error: 'error',
} as const;
type GateStatus = (typeof GATE_STATUS)[keyof typeof GATE_STATUS];

const MerchantTimezoneContext = createContext<MerchantTimezoneContextValue | null>(null);

/**
 * The time zone GATE (#2875, ADR-0011 decision seven). It renders its subtree
 * only after the session tenant's zone has arrived from `/api/system/timezone`
 * and has been installed with `setMerchantTz`, so nothing below it ever reads
 * `getMerchantTz()` before the zone is set, and no date renders in a fallback
 * zone. It must render inside `BetterAuthProvider`, below the sign-in guard.
 *
 *   - loading → the full-screen loading status, no children;
 *   - error   → a full-screen block with a retry button, no children;
 *   - ready   → the context provider and the children.
 *
 * Keyed on `userId` via the query key: when the user (and so the tenant)
 * changes, the new key has no data, the gate goes back to loading, the subtree
 * unmounts, and it mounts again only with the new tenant's zone. Background
 * refetch keeps the subtree mounted: invalidating `sessionTimezone` (#2918)
 * reads the zone again in place, and a failed refetch keeps the installed zone.
 */
export function MerchantTimezoneProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { user, loading } = useBetterAuth();
  const userId = user?.id ?? null;
  const query = useQuery({
    queryKey: queryKeys.sessionTimezone.forUser(userId),
    queryFn: () => http.get<TimezoneSnapshot>(SESSION_TIMEZONE_PATH),
    enabled: !loading,
    // On the query, not the client: the app client's transient retries would
    // hold the error block back until they run out.
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const { data } = query;

  // Installed here, in render, from this user's key: a late response for a
  // previous user lands under that user's key and is never read. `http.get`
  // already unwraps the `{ data }` success envelope. A name `setMerchantTz`
  // refuses installs nothing, so it is no zone either: going ready would leave
  // `getMerchantTz()` unset or on the previous tenant's zone.
  const installed = data !== undefined && !!data.timezone && setMerchantTz(data.timezone);

  let status: GateStatus = GATE_STATUS.loading;
  if (data !== undefined) {
    status = installed ? GATE_STATUS.ready : GATE_STATUS.error;
  } else if (query.isError) {
    status = GATE_STATUS.error;
  }

  if (status === GATE_STATUS.error) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="alert">
        <div className="flex flex-col items-center gap-3">
          <p className="text-muted-foreground">{t('ui.timezoneLoadFailed')}</p>
          <Button type="button" variant="outline" onClick={() => void query.refetch()}>
            {t('ui.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (status !== GATE_STATUS.ready || data === undefined) {
    return <AuthLoadingFallback variant="full" />;
  }

  return (
    <MerchantTimezoneContext.Provider
      value={{
        timezone: data.timezone,
        abbreviation: data.abbreviation,
        offset: data.offset,
      }}
    >
      {children}
    </MerchantTimezoneContext.Provider>
  );
}

/**
 * A fixed context value with no gate and no request — for a harness that does
 * not mount the app (the design-sync preview, `ds-providers.tsx`), which sets
 * the matching zone with `setMerchantTz` itself.
 */
export function StaticMerchantTimezoneProvider({
  value,
  children,
}: {
  value: MerchantTimezoneContextValue;
  children: ReactNode;
}) {
  return (
    <MerchantTimezoneContext.Provider value={value}>{children}</MerchantTimezoneContext.Provider>
  );
}

export function useMerchantTimezone(): MerchantTimezoneContextValue {
  const context = useContext(MerchantTimezoneContext);
  if (!context) {
    throw new Error('useMerchantTimezone must be used within MerchantTimezoneProvider');
  }
  return context;
}
